import type { ValidationError } from 'yup';

/**
 * Turns a form's validation errors into a list a user can read.
 *
 * A yup error says `locations[1].manualAddress.city`, which is of no use to anyone looking at the
 * screen. This module renders that as "Stop 2: Brno › City" by walking the path one segment at a
 * time against the form's own values, so indexes become the names of the things they point at.
 *
 * Nothing here is specific to one form: a form supplies a {@link ValidationSummaryConfig} saying how
 * its segments are named and how its paths divide into groups. Kept free of React so the rules can
 * be unit tested on their own.
 */

export type Translate = (key: string, defaultText?: string, data?: any, count?: number) => string;

export interface SummaryRow {
  /** Stable identity for rendering; also what duplicate rows are collapsed on. */
  key: string;
  group: string;
  /** Sorts the groups against each other. Lower comes first. */
  groupOrder: number;
  /** Absent when the message stands on its own, as a whole-form or server message does. */
  label?: string;
  message: string;
}

export interface SummaryGroup {
  group: string;
  rows: SummaryRow[];
}

export type PathToken = { kind: 'name'; name: string } | { kind: 'index'; index: number };

/** `locations[1].manualAddress.city` → name locations, index 1, name manualAddress, name city. */
export const tokenizePath = (path: string): PathToken[] =>
  (path?.match(/[^.[\]]+/g) ?? []).map(part => (/^\d+$/.test(part) ? { kind: 'index', index: Number(part) } : { kind: 'name', name: part }));

export interface GroupRef {
  name: string;
  order: number;
  /** Leading tokens the group already accounts for, so the label does not repeat them. */
  consumed: number;
}

export interface ValidationSummaryConfig {
  /** Leaf segment → English fallback. Its i18n key is `${labelKeyPrefix}.${segment}`. */
  labels: Record<string, string>;
  labelKeyPrefix: string;
  /**
   * Segment → how to name one entry of it, given the entry itself. Used for the array segments that
   * a user thinks of by name rather than by position ("Order ABC-1", not "Orders 3").
   */
  collections?: Record<string, (index: number, item: any, tr: Translate) => string>;
  /** Which group a path belongs to. Returning null falls back to {@link defaultGroup}. */
  resolveGroup?: (tokens: PathToken[], values: any, tr: Translate) => GroupRef | null;
  defaultGroup: (tr: Translate) => { name: string; order: number };
  /** Message per yup error type, for the rules whose schema left none. */
  genericMessages?: Record<string, string>;
}

const DEFAULT_GENERIC_MESSAGES: Record<string, string> = {
  required: 'This field is required',
  typeError: 'Value has the wrong format',
  min: 'Value is too small',
  max: 'Value is too large',
  moreThan: 'Value is too small',
  email: 'Value has to be a valid e-mail address',
};

/** `mainContactEmail` → `Main contact email`, for segments the config does not name. */
const humanise = (segment: string): string =>
  segment
    .replace(/([A-Z])/g, character => ` ${character.toLowerCase()}`)
    .replace(/^./, character => character.toUpperCase())
    .trim();

const labelForSegment = (segment: string, config: ValidationSummaryConfig, tr: Translate): string => {
  const fallback = config.labels[segment];
  return fallback ? tr(`${config.labelKeyPrefix}.${segment}`, fallback) : humanise(segment);
};

/**
 * The message to show for one error.
 *
 * Yup's built-in messages embed the raw path — "locations[0].city is a required field" — which reads
 * as nonsense in a list that already says which field it means. Those are swapped for a translated
 * generic; a message the schema set deliberately is kept as it is.
 */
export const resolveMessage = (error: ValidationError, tr: Translate, genericMessages: Record<string, string> = DEFAULT_GENERIC_MESSAGES): string => {
  const type = error.type ?? 'required';
  const raw = error.message;
  const isYupDefault = !raw || (!!error.path && raw.includes(error.path));
  if (!isYupDefault) return raw;
  const generic = genericMessages[type] ?? genericMessages.required ?? DEFAULT_GENERIC_MESSAGES.required;
  return tr(`validation.${type}`, generic);
};

/**
 * The human name of a path, minus the part its group already states.
 *
 * Only the final segment names the field. A named segment in the middle — `manualAddress`, `price`,
 * `timeslots` — is structure the user never sees and drops out. A named segment followed by an index
 * is the exception: that pair is one of a collection, and it is named as such.
 */
export const describePath = (tokens: PathToken[], values: any, config: ValidationSummaryConfig, tr: Translate, consumed: number = 0): string => {
  const parts: string[] = [];
  let cursor: any = values;
  let position = 0;

  for (; position < consumed && position < tokens.length; position++) {
    const token = tokens[position];
    cursor = token.kind === 'index' ? cursor?.[token.index] : cursor?.[token.name];
  }

  for (; position < tokens.length; position++) {
    const token = tokens[position];
    if (token.kind === 'index') continue;

    cursor = cursor?.[token.name];
    const next = tokens[position + 1];

    if (next?.kind === 'index') {
      const item = cursor?.[next.index];
      const namer = config.collections?.[token.name];
      parts.push(namer ? namer(next.index, item, tr) : `${labelForSegment(token.name, config, tr)} ${next.index + 1}`);
      cursor = item;
      position++;
      continue;
    }

    if (position === tokens.length - 1) parts.push(labelForSegment(token.name, config, tr));
  }

  return parts.join(' › ');
};

/** One row per validation error, in the order the errors came in. */
export const buildValidationRows = (errors: ValidationError[], values: any, tr: Translate, config: ValidationSummaryConfig): SummaryRow[] => {
  if (!errors?.length) return [];

  return errors
    .filter(error => error != null)
    .map(error => {
      const tokens = tokenizePath(error.path ?? '');
      const group = config.resolveGroup?.(tokens, values, tr) ?? null;
      const resolved = group ?? { ...config.defaultGroup(tr), consumed: 0 };
      const label = describePath(tokens, values, config, tr, resolved.consumed);

      return {
        key: error.path || `form-${error.type ?? 'error'}`,
        group: resolved.name,
        groupOrder: resolved.order,
        label: label || undefined,
        message: resolveMessage(error, tr, config.genericMessages),
      };
    });
};

/**
 * Groups rows for display, dropping the ones that would say the same thing twice.
 *
 * A single mistake often trips more than one rule — `cargoType` and `cargoType.cargoTypeCode` both
 * fire when no cargo type is picked — and both resolve to the same group and label. Only the first
 * survives; listing a field twice reads as two separate problems to fix.
 */
export const groupSummaryRows = (rows: SummaryRow[]): SummaryGroup[] => {
  const seen = new Set<string>();
  const byGroup = new Map<string, { order: number; rows: SummaryRow[] }>();

  rows.forEach(row => {
    const identity = `${row.group}|${row.label ?? ''}|${row.label ? '' : row.message}`;
    if (seen.has(identity)) return;
    seen.add(identity);

    const existing = byGroup.get(row.group);
    if (existing) existing.rows.push(row);
    else byGroup.set(row.group, { order: row.groupOrder, rows: [row] });
  });

  return Array.from(byGroup.entries())
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([group, { rows: groupRows }]) => ({ group, rows: groupRows }));
};

/** The whole job for a plain form: errors in, groups out. */
export const buildValidationSummary = (errors: ValidationError[], values: any, tr: Translate, config: ValidationSummaryConfig): SummaryGroup[] =>
  groupSummaryRows(buildValidationRows(errors, values, tr, config));
